import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Award, Clock } from 'lucide-react';
import { TopAnalyst } from '@/hooks/useDashboardMetrics';

interface TopAnalystsProps {
  analysts: TopAnalyst[] | undefined;
  isLoading: boolean;
}

const TopAnalysts = ({ analysts, isLoading }: TopAnalystsProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((j) => (
              <div key={j} className="flex justify-between items-center animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const getPositionIcon = (index: number) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `${index + 1}º`;
  };

  const getPositionColor = (index: number) => {
    if (index === 0) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (index === 1) return 'text-gray-600 bg-gray-50 border-gray-200';
    if (index === 2) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  const getPerformanceBadge = (avgTime: number) => {
    if (avgTime <= 2) return { color: 'bg-green-100 text-green-800', label: 'Excelente' };
    if (avgTime <= 4) return { color: 'bg-blue-100 text-blue-800', label: 'Bom' };
    if (avgTime <= 8) return { color: 'bg-yellow-100 text-yellow-800', label: 'Regular' };
    return { color: 'bg-red-100 text-red-800', label: 'Lento' };
  };

  return (
    <Card className="hover:shadow-lg transition-all duration-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-green-600">
          <Users className="h-5 w-5" />
          Analistas Mais Produtivos
        </CardTitle>
      </CardHeader>
      <CardContent>
        {analysts && analysts.length > 0 ? (
          <div className="space-y-4">
            {analysts.map((analyst, index) => {
              const performanceBadge = getPerformanceBadge(analyst.avgTime);
              return (
                <div 
                  key={analyst.name || `analyst-${index}`} 
                  className={`p-3 rounded-lg border transition-all duration-200 hover:shadow-md ${getPositionColor(index)}`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold">
                          {getPositionIcon(index)}
                        </span>
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-xs font-semibold text-white">
                            {analyst.name.split(' ').map(n => n.charAt(0)).join('').substring(0, 2)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="font-medium text-sm truncate max-w-[120px]">
                          {analyst.name}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`text-xs px-2 py-0.5 ${performanceBadge.color}`}>
                            {performanceBadge.label}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm font-bold">
                        <Award className="h-3 w-3" />
                        {analyst.resolved}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {analyst.avgTime}h média
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Resumo */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Total Resolvidos
                </span>
                <span className="font-bold">
                  {analysts.reduce((sum, analyst) => sum + analyst.resolved, 0)}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-600">
                  Tempo médio geral
                </span>
                <span className="text-xs font-semibold text-green-600">
                  {(analysts.reduce((sum, analyst) => sum + analyst.avgTime, 0) / analysts.length).toFixed(1)}h
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Nenhum analista encontrado</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TopAnalysts;
